import * as THREE from 'three';

const QUARTER = Math.PI / 2;
const BUTTON_DURATION_MS = 400;
const SNAP_THRESHOLD = 0.05;
const INERTIA_FRICTION = 0.92;
const MIN_VELOCITY = 0.002;
const BUTTON_ROTATE_SPEED = 2.4;

export function snapToQuarter(angle: number): number {
  return Math.round(angle / QUARTER) * QUARTER;
}

export function normalizeAngle(angle: number): number {
  let a = angle % (Math.PI * 2);
  if (a > Math.PI) a -= Math.PI * 2;
  if (a < -Math.PI) a += Math.PI * 2;
  return a;
}

export class RotationController {
  private target = new THREE.Group();
  private currentY = 0;
  private currentX = -0.45;
  private velocity = 0;
  private animating = false;
  private animStart = 0;
  private animFrom = 0;
  private animTo = 0;
  private dragLastX = 0;
  private dragLastY = 0;
  private isDragging = false;
  private buttonDirection: -1 | 1 | 0 = 0;
  private lastTickTime = 0;
  private blocked = false;
  private readonly reducedMotion: boolean;

  constructor(
    boardGroup: THREE.Group,
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ) {
    this.target = boardGroup;
    this.reducedMotion = reducedMotion;
  }

  getBoardGroup(): THREE.Group {
    return this.target;
  }

  getRotationY(): number {
    return this.currentY;
  }

  setBlocked(blocked: boolean): void {
    this.blocked = blocked;
    if (blocked) {
      this.isDragging = false;
      this.velocity = 0;
      this.buttonDirection = 0;
    }
  }

  isAnimating(): boolean {
    return this.animating;
  }

  stepRotateByButton(direction: -1 | 1): void {
    if (this.blocked || this.animating) return;
    const to = snapToQuarter(this.currentY) + direction * QUARTER;
    this.startAnimation(to);
  }

  startButtonRotate(direction: -1 | 1): void {
    if (this.blocked || this.isDragging) return;
    this.animating = false;
    this.velocity = 0;
    this.buttonDirection = direction;
  }

  stopButtonRotate(): void {
    this.buttonDirection = 0;
    if (this.blocked || this.isDragging) return;
    this.snapNow();
  }

  startDrag(clientX: number): void {
    if (this.blocked || this.animating) return;
    this.isDragging = true;
    this.dragLastX = clientX;
    this.dragLastY = 0;
    this.velocity = 0;
  }

  setDragStartY(clientY: number): void {
    this.dragLastY = clientY;
  }

  updateDrag(clientX: number, clientY: number): void {
    if (!this.isDragging || this.blocked) return;
    const delta = clientX - this.dragLastX;
    const deltaY = clientY - this.dragLastY;
    this.dragLastX = clientX;
    this.dragLastY = clientY;
    const rotationDelta = delta * 0.008;
    this.currentY += rotationDelta;
    this.currentX = THREE.MathUtils.clamp(this.currentX - deltaY * 0.006, -1.25, 0.25);
    this.velocity = rotationDelta;
    this.applyRotation();
  }

  endDrag(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    if (this.reducedMotion) {
      this.snapNow();
      return;
    }
  }

  snapNow(): void {
    const snapped = snapToQuarter(this.currentY);
    if (this.reducedMotion) {
      this.currentY = snapped;
      this.velocity = 0;
      this.applyRotation();
      return;
    }
    this.startAnimation(snapped);
  }

  private startAnimation(to: number): void {
    if (this.reducedMotion) {
      this.currentY = to;
      this.velocity = 0;
      this.applyRotation();
      return;
    }
    this.animating = true;
    this.animStart = performance.now();
    this.animFrom = this.currentY;
    this.animTo = to;
    this.velocity = 0;
  }

  tick(now: number): void {
    if (this.lastTickTime === 0) {
      this.lastTickTime = now;
    }
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickTime) / 1000));
    this.lastTickTime = now;

    if (this.buttonDirection !== 0 && !this.blocked && !this.isDragging) {
      this.currentY += this.buttonDirection * BUTTON_ROTATE_SPEED * dt;
      this.applyRotation();
      return;
    }

    if (this.animating) {
      const elapsed = now - this.animStart;
      const t = Math.min(1, elapsed / BUTTON_DURATION_MS);
      const eased = easeOutBack(t);
      this.currentY = this.animFrom + (this.animTo - this.animFrom) * eased;
      this.applyRotation();
      if (t >= 1) {
        this.animating = false;
        this.currentY = this.animTo;
        this.applyRotation();
      }
      return;
    }

    if (this.isDragging || this.blocked) return;

    if (Math.abs(this.velocity) > MIN_VELOCITY) {
      this.currentY += this.velocity;
      this.velocity *= INERTIA_FRICTION;
      this.applyRotation();
      return;
    }

    const snapped = snapToQuarter(this.currentY);
    if (Math.abs(normalizeAngle(this.currentY - snapped)) > SNAP_THRESHOLD) {
      this.startAnimation(snapped);
    } else {
      this.currentY = snapped;
      this.applyRotation();
    }
  }

  private applyRotation(): void {
    this.target.rotation.order = 'YXZ';
    this.target.rotation.x = this.currentX;
    this.target.rotation.y = this.currentY;
  }
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
}
